import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PageLayout } from "@/components/PageLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

// Safe boolean expression evaluator without eval()
const evaluateExpression = (expr: string, values: { [key: string]: boolean }): boolean => {
  // Tokenize the expression
  let tokens = expr.toUpperCase();
  
  // Replace variables with their boolean values
  Object.entries(values).forEach(([variable, value]) => {
    tokens = tokens.replace(new RegExp(variable, 'g'), value ? 'T' : 'F');
  });
  
  // Replace logical operators with standardized symbols
  tokens = tokens.replace(/∧/g, "AND");
  tokens = tokens.replace(/∨/g, "OR");
  tokens = tokens.replace(/¬/g, "NOT");
  tokens = tokens.replace(/NOT\s+/g, "NOT_");
  
  // Parse and evaluate using a safe recursive descent parser
  let position = 0;
  
  const peek = (): string => tokens[position] || '';
  const consume = (): string => tokens[position++] || '';
  const skipWhitespace = () => {
    while (position < tokens.length && /\s/.test(tokens[position])) {
      position++;
    }
  };
  
  const parseAtom = (): boolean => {
    skipWhitespace();
    
    // Handle NOT operator
    if (tokens.substring(position, position + 4) === 'NOT_') {
      position += 4;
      return !parseAtom();
    }
    
    // Handle parentheses
    if (peek() === '(') {
      consume(); // consume '('
      const result = parseOr();
      skipWhitespace();
      if (peek() === ')') {
        consume(); // consume ')'
      }
      return result;
    }
    
    // Handle boolean literals
    if (peek() === 'T') {
      consume();
      return true;
    }
    if (peek() === 'F') {
      consume();
      return false;
    }
    
    return false;
  };
  
  const parseAnd = (): boolean => {
    let result = parseAtom();
    skipWhitespace();
    
    while (tokens.substring(position, position + 3) === 'AND') {
      position += 3;
      skipWhitespace();
      result = result && parseAtom();
      skipWhitespace();
    }
    
    return result;
  };
  
  const parseOr = (): boolean => {
    let result = parseAnd();
    skipWhitespace();
    
    while (tokens.substring(position, position + 2) === 'OR') {
      position += 2;
      skipWhitespace();
      result = result || parseAnd();
      skipWhitespace();
    }
    
    return result;
  };
  
  try {
    return parseOr();
  } catch {
    return false;
  }
};

const TruthTableCalculator = () => {
  const [expression, setExpression] = useState("");
  const [truthTable, setTruthTable] = useState<any[]>([]);
  const [variables, setVariables] = useState<string[]>([]);

  const extractVariables = (expr: string): string[] => {
    const vars = expr.match(/[A-Z]/g) || [];
    return [...new Set(vars)].sort();
  };

  const generateTruthTable = () => {
    const vars = extractVariables(expression.toUpperCase());
    setVariables(vars);
    
    if (vars.length === 0) {
      setTruthTable([]);
      return;
    }
    
    const rows = Math.pow(2, vars.length);
    const table = [];
    
    for (let i = 0; i < rows; i++) {
      const row: any = {};
      
      // Generate binary values for each variable
      vars.forEach((variable, index) => {
        row[variable] = Boolean((i >> (vars.length - 1 - index)) & 1);
      });
      
      // Evaluate the expression
      row.result = evaluateExpression(expression, row);
      table.push(row);
    }
    
    setTruthTable(table);
  };

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Truth Table Generator</CardTitle>
            <CardDescription>Generate truth tables for logical expressions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="expression">Logical Expression</Label>
              <Input
                id="expression"
                placeholder="Enter expression (e.g., A AND B OR NOT C)"
                value={expression}
                onChange={(e) => setExpression(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Use: AND (∧), OR (∨), NOT (¬) with capital letters A-Z as variables
              </p>
            </div>
            
            <Button onClick={generateTruthTable} className="w-full">
              Generate Truth Table
            </Button>
            </CardContent>
        </Card>

        {truthTable.length > 0 && (
          <>
            <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Truth Table</h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {variables.map((variable) => (
                      <TableHead key={variable} className="text-center">{variable}</TableHead>
                    ))}
                    <TableHead className="text-center font-bold">Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {truthTable.map((row, index) => (
                    <TableRow key={index}>
                      {variables.map((variable) => (
                        <TableCell key={variable} className="text-center">
                          {row[variable] ? "T" : "F"}
                        </TableCell>
                      ))}
                      <TableCell className="text-center font-bold">
                        {row.result ? "T" : "F"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          <CalculatorSuperCalcCTA config={calculatorCTAs["truth-table"]} trigger={truthTable.length} />
          </>
        )}

        <Card className="p-6 bg-muted/50">
          <h3 className="font-semibold mb-2">How to Use</h3>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• Use capital letters A-Z as variables</li>
            <li>• Supported operators: AND, OR, NOT</li>
            <li>• Use parentheses for grouping: (A AND B) OR C</li>
            <li>• Example: A AND (B OR NOT C)</li>
          </ul>
        </Card>
      </div>
    </PageLayout>
  );
};

export default TruthTableCalculator;
